import * as React from 'react'
import { graphql } from 'gatsby'
import MastHead from '../components/organisms/MastHead/masthead'
import TypographyAtom from '../components/atoms/typography/typography'
import CalloutCTA from '../components/organisms/CalloutCTA/CalloutCTA'
import SectionHeader from '../components/organisms/SectionHeader/SectionHeader'
import ContactForm from '../components/organisms/Forms/ContactForm'
import Breadcrumbs from '../components/organisms/Breadcrumbs/Breadcrumbs'
import getCalloutLink from '../helpers/getCalloutLink'
import isOuterLink from '../helpers/isOuterLink'
import { createWebpageSchema } from '../helpers/createWebpageSchema'
import MetaTwitterCard from '../components/organisms/MetaTwitterCard/MetaTwitterCard'

// Gatsby Head
//---------------------------------
export function Head({ data, pageContext, location }) {
  const title = data.contentfulPageSubLevel.pageMetaTitle
  const description = data.contentfulPageSubLevel.pageMetaDescription
  const ogTitle = data?.contentfulPageSubLevel?.mastheadTitleLine || title

  const webPageSchema = createWebpageSchema({
    pageTitle: title,
    pageDescription: description,
    pageUrl: `${process.env.GATSBY_HELIGUY_DOMAIN}${location?.pathname}`,
    breadcrumbs: pageContext?.breadCrumbs,
  })

  const pageObjectUrl =
    data?.contentfulPageSubLevel?.mastheadBackgroundImage?.url || 'https://placehold.co/400x400'
  const pageObjectUrlWidth =
    data?.contentfulPageSubLevel?.mastheadBackgroundImage?.gatsbyImageData?.width || '400'
  const pageObjectUrlHeight =
    data?.contentfulPageSubLevel?.mastheadBackgroundImage?.gatsbyImageData?.height || '400'

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <html lang="en" />
      <meta charset="UTF-8" />
      <meta name="keywords" content="" />
      <meta name="robots" content="index, follow" />
      <meta name="author" content="Heliguy" />
      <meta property="og:locale" content="en" />
      <meta property="og:site_name" content="Heliguy™" />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={pageObjectUrl} />
      <meta property="og:image:width" content={pageObjectUrlWidth} />
      <meta property="og:image:height" content={pageObjectUrlHeight} />
      <meta
        property="og:url"
        content={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
      />
      <MetaTwitterCard
        title={ogTitle}
        mainImage={pageObjectUrl}
        pageUrl={`${process.env.GATSBY_HELIGUY_DOMAIN}${location.pathname}`}
        pageDescription={description}
      />
      <link rel="preconnect" href="https://images.ctfassets.net" crossOrigin />
      <link
        rel="canonical"
        href={`${process.env.GATSBY_HELIGUY_DOMAIN}${pageContext.breadcrumb.location}`}
      />
      {webPageSchema && <script type="application/ld+json">{webPageSchema}</script>}
    </>
  )
}

// Page Export
//---------------------------------
function SubLevelPageTemplate({ data, pageContext }) {
  const {
    breadcrumb: { crumbs },
  } = pageContext

  const isPartiallyActive = ({ isPartiallyCurrent, isCurrent }) =>
    isPartiallyCurrent && isCurrent
      ? { className: 'breadcrumb__link breadcrumb__link__active' }
      : {}

  const liveData = data

  const calloutButtonLink = getCalloutLink(liveData?.contentfulPageSubLevel.ctaButtonHref)

  return (
    <>
      <div className="templateWrapper templateWrapper--alt">
        <section className="outer">
          <div className="inner">
            <Breadcrumbs
              crumbs={pageContext?.breadCrumbs || []}
              crumbSeparator=" - "
              // crumbLabel={liveData.contentfulPageSubLevel.pageMetaTitle}
              getProps={isPartiallyActive}
            />
          </div>
        </section>
        <MastHead
          imgSrc={liveData.contentfulPageSubLevel.mastheadBackgroundImage.gatsbyImageData}
          title={liveData.contentfulPageSubLevel.mastheadTitleLine}
          subtitle={liveData.contentfulPageSubLevel.mastheadSubtitle}
          type="Secondary"
        />
        <section className="outer">
          <div className="inner">
            <div className="row between-md">
              <div className="col-xs-12 col-md-7">
                {liveData.contentfulPageSubLevel.textBlocks.map((target, index) => (
                  <div className="margin-80" key={index}>
                    <SectionHeader
                      className="margin-24"
                      index={index}
                      headerCopy={target.sectionHeader}
                    />
                    <TypographyAtom
                      type="BlockText"
                      copyBlock={target.bodyText}
                      weight="BlockText"
                    />
                  </div>
                ))}
              </div>
              <aside className="col-xs-12 col-md-4">
                {liveData.contentfulPageSubLevel.ctaCalloutTitle && (
                  <div className="margin-80">
                    <CalloutCTA
                      fieldsNames={{
                        title: 'ctaCalloutTitle',
                        body: 'ctaCalloutBody',
                        button: 'ctaCalloutButtonText',
                      }}
                      title={liveData.contentfulPageSubLevel.ctaCalloutTitle}
                      body={liveData.contentfulPageSubLevel.ctaCalloutBody}
                      buttonCopy={liveData.contentfulPageSubLevel.ctaCalloutButtonText}
                      linkType={isOuterLink(calloutButtonLink) ? '_blank' : '_self'}
                      rel={isOuterLink(calloutButtonLink) ? 'noopener noreferrer' : ''}
                      linkUrl={calloutButtonLink}
                    />
                  </div>
                )}
                <ContactForm />
              </aside>
            </div>
          </div>
        </section>
      </div>{' '}
    </>
  )
}

export default SubLevelPageTemplate

// GraphQL Call
//---------------------------------
export const data = graphql`
  query ($slug: String!) {
    contentfulPageSubLevel(pageSlug: { eq: $slug }) {
      __typename
      contentful_id
      pageSlug
      pageMetaTitle
      pageMetaDescription
      mastheadTitleLine
      mastheadSubtitle
      mastheadBackgroundImage {
        gatsbyImageData(formats: WEBP)
        url
      }
      ctaCalloutTitle
      ctaCalloutBody {
        raw
      }
      ctaButtonHref
      ctaCalloutButtonText
      textBlocks {
        contentful_id
        sectionHeader
        bodyText {
          raw
          references {
            ... on Node {
              ... on ContentfulAsset {
                __typename
                gatsbyImageData(width: 1920)
                contentful_id
                file {
                  url
                }
              }
            }
          }
        }
      }
    }
  }
`
